import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useTheme } from '../contexts/ThemeContext'; // Import useTheme hook
import TopBar from './TopBar';
import Sidebar from './Sidebar';

export const PrivateRoute = ({ children, excludeRoles = [] }) => {
  const { currentUser, userRole, userLandingPage, loading, initializing } = useAuth();
  const { isCollapsed, isMobileOpen } = useSidebar();
  const { isDarkMode } = useTheme(); // Use the isDarkMode variable from the ThemeContext
  const location = useLocation();

  // If still initializing, show loading spinner
  if (initializing) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: isDarkMode ? '#0F172A' : '#FFFFFF',
          color: isDarkMode ? '#FFFFFF' : '#1F1F1F'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not logged in, redirect to login
  if (!loading && !currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user role is excluded, redirect to user's landing page
  if (!loading && excludeRoles.includes(userRole)) {
    return <Navigate to={userLandingPage || '/dashboard'} replace />;
  }

  // Show loading spinner while still loading
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: isDarkMode ? '#0F172A' : '#FFFFFF',
          color: isDarkMode ? '#FFFFFF' : '#1F1F1F'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render the protected content
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: isDarkMode ? '#0F172A' : '#FFFFFF',
        color: isDarkMode ? '#FFFFFF' : '#1F1F1F'
      }}
    >
      <TopBar />
      <Box sx={{ display: 'flex', flex: 1, pt: '64px' }}>
        <Sidebar />
        <Box
          component="main"
          className="main-content"
          sx={{
            flexGrow: 1,
            marginLeft: 0,
            paddingLeft: {
              xs: '16px',
              sm: '24px'
            },
            paddingRight: {
              xs: '12px',
              sm: '20px'
            },
            transition: 'margin-left 0.3s ease, padding 0.3s ease',
            minHeight: 'calc(100vh - 64px)', // Subtract the height of TopBar
            width: '100%',
            overflow: 'hidden',
            bgcolor: 'transparent',
            position: 'relative',
            zIndex: 10,
            '@media (min-width: 1024px)': {
              marginLeft: '280px',
              paddingLeft: '32px',
              paddingRight: '32px'
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: isDarkMode ? '#0F172A' : '#FFFFFF',
              zIndex: -1
            }
          }}
        >
          <Box 
            sx={{ 
              flex: 1, 
              p: { xs: 2, sm: 2.5, md: 3 },
              bgcolor: 'transparent'
            }}
          >
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100%',
                bgcolor: 'transparent'
              }}>
                <CircularProgress />
              </Box>
            ) : (
              children
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
